import i18nFactory from '@yandex-int/i18n';
import { filter, find, get, map, reduce } from 'lodash';
import React from 'react';
import { Action } from 'redux';

import { openCryptaBehavioralEditor } from '../../actions/targetings/cryptaBehavioral';
import { openCryptaGroupFamilyEditor } from '../../actions/targetings/cryptaGroupFamily';
import { openCryptaSocialDemoEditor } from '../../actions/targetings/cryptaSocialDemo';
import { openInterestsTargetingEditor } from '../../actions/targetings/interestsEditor';
import { BetaMark } from '../../components/BetaMark/BetaMark';
import { CryptaSegmentGroup } from '../../components/CryptaSocialDemo/CryptaSocialDemo';
import { InterestsIdsType, InterestsTreeType } from '../../components/InterestsSetEditor/InterestsSetEditor';
import { defaultInterestsTargetingDisabledSetState } from '../../reducers/targetings/interestsEditor';
import { AudienceTargetingsEditorDescription, AudienceTargetingsType } from '../../typings/audienceTargetings';
import {
    PreviewTextValue,
    SegmentCategory,
    SegmentCategoryText,
    SegmentSelector
} from '../../typings/crypta-segment-selector';
import { CryptaSegment } from '../../typings/cryptaSegments';
import { CampaignType } from '../../typings/domain';
import { CryptaInterestTypeWeb, WebGoalType } from '../../typings/graphql-grid';
import { GroupId } from '../../typings/groups';
import { EditGroupDataValue } from '../../typings/groupsEditState';
import { InterestsSetType } from '../../typings/interestsEditor';
import { TextPredictor } from '../../typings/predictor';
import {
    RetargetingConditionData,
    RetargetingConditionDataRulesGroup,
    RetargetingConditionRuleType
} from '../../typings/retargeting';
import { UserProfileRetargetingConditionGoalType } from '../../typings/userProfileRetargeting';
import { Bem } from '../Bem/Bem';
import { getGoalNameWithDomain } from '../GoalsHelpers/GoalsHelpers';
import { getRetargetingConditionTypeText } from '../RetargetingHelpers/RetargetingHelpers';
import { getUserProfileConditionTitle } from '../UserProfileHelpers/UserProfileHelpers';

import * as keyset from './CryptaHelpers.i18n';

const i18n = i18nFactory(keyset);

const SIGNS_COMPACT = 7; // количество знаков добовляемых к строке (,)( )( )(·)( )(:)( )

const interestTitleByPeriod = {
    all: i18n('all-interest'),
    short_term: i18n('short-term-interest'),
    long_term: i18n('long-term-interest')
};

export const mapRawCryptaSegmentsToSegmentsGroup = (segmentsGroupProps: CryptaSegment[]): CryptaSegmentGroup => {
    let familyCategory;

    // группируем категории по алфавиту только для family
    if (segmentsGroupProps.length && segmentsGroupProps[0].type === WebGoalType.family) {
        familyCategory = segmentsGroupProps.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {
                sensitivity: 'base',
                numeric: true
            })
        );
    }

    return reduce(
        familyCategory ? familyCategory : segmentsGroupProps,
        (segmentsGroup: SegmentCategory[], segment: CryptaSegment) => {
            if (!Number(segment.parentId)) {
                segmentsGroup[segment.id] = {
                    title: segment.name,
                    canSelectAll: segment.canSelectAll,
                    selectors: (segmentsGroup[segment.id] && [...segmentsGroup[segment.id].selectors]) || []
                };
            } else if (segment.parentId) {
                segmentsGroup[segment.parentId] = segmentsGroup[segment.parentId] || {
                    selectors: []
                };

                segmentsGroup[segment.parentId].selectors.push({
                    id: String(segment.id),
                    name: segment.name
                });
            }

            return segmentsGroup;
        },
        {}
    );
};

export const getCheckedSegmentsVal = (
    segment: SegmentCategory,
    val: keyof SegmentSelector,
    checkedSegments: string[]
): string[] => {
    const checkedSegmentList = filter(segment.selectors, (selector: SegmentSelector) =>
        checkedSegments.includes(selector.id)
    );

    return map(checkedSegmentList, (selector: SegmentSelector) => selector[val]);
};

export const getPreviewSegment = (
    segmentsGroup: CryptaSegmentGroup,
    checkedSegments: string[]
): SegmentCategoryText[] => {
    return Object.keys(segmentsGroup).reduce((segments: SegmentCategoryText[], key: string) => {
        const segment = segmentsGroup[key];
        const segmentsItems = getCheckedSegmentsVal(segment, 'name', checkedSegments);

        if (segmentsItems.length > 0) {
            const count =
                segment.title.length +
                segmentsItems.reduce((countExtra: number, element: string) => countExtra + element.length, 0);

            segments.push({
                title: segment.title,
                segments: segmentsItems,
                count
            });
        }

        return segments;
    }, []);
};

export const getPreviewText = (
    textPreview: SegmentCategoryText[],
    BLOCK: string,
    hideSubtitle?: boolean
): JSX.Element | null => {
    if (textPreview.length <= 0) {
        return null;
    }

    return (
        <>
            {textPreview.map((segment: SegmentCategoryText, index: number) => (
                <span key={index}>
                    {hideSubtitle ||
                        (segment.title && (
                            <span className={Bem.cls(BLOCK, 'subtitle')}>
                                {Boolean(index) && ' · '}
                                {`${segment.title}: `}
                            </span>
                        ))}
                    {segment.segments.join(', ')}
                </span>
            ))}
        </>
    );
};

export const getPreviewTextCompact = (
    value: PreviewTextValue
): {
    hide: boolean;
    shortContent: JSX.Element;
    limits: number;
} => {
    const { textPreview, BLOCK, hideSubtitle, isLastLine } = value;

    let { limits } = value;

    const result: JSX.Element[] = [];

    const hideDot = textPreview.every((segment: SegmentCategoryText, index: number) => {
        const { count = 0 } = segment;
        const segments = segment.segments.join(', ');
        let shortValue = segments;

        limits -= count + SIGNS_COMPACT;

        // обрезаем по целым словам
        if (limits <= 0) {
            shortValue = segments.slice(0, limits);
            const lastIndexShortValue = shortValue.lastIndexOf(', ');
            const lastIndexFullValue = segments.lastIndexOf(', ');

            // если это последняя фраза после запятой в последней строке, то не обрезаем
            if (isLastLine && index === textPreview.length - 1 && lastIndexShortValue === lastIndexFullValue) {
                shortValue = segments;
                limits = 1;
            } else {
                shortValue = segments.slice(0, shortValue.lastIndexOf(', ') + 1);
            }
        }

        result.push(
            <span key={index}>
                {hideSubtitle ||
                    (segment.title && (
                        <span className={Bem.cls(BLOCK, 'subtitle')}>
                            {Boolean(index) && ' · '}
                            {`${segment.title}: `}
                        </span>
                    ))}
                {shortValue}
            </span>
        );

        return limits > 0;
    });

    return {
        hide: hideDot,
        shortContent: (
            <>
                {result}
                {!hideDot && '...'}
            </>
        ),
        limits
    };
};

export const transformInterestsToSegments = (
    interestsSets: InterestsSetType[],
    interestsIds: InterestsIdsType,
    isFullTitle: boolean
): SegmentCategoryText[] => {
    return interestsSets.reduce((acc: SegmentCategoryText[], interest: InterestsSetType, key: number) => {
        const prefixText = i18n(interest.period);
        const interests = `${i18n('interests-set')} ${key + 1}`;
        const text = interest.checkedList.map(id => interestsIds[id].name);
        const countText = text.reduce((countExtra: number, element: string) => countExtra + element.length, 0);

        if (text.length !== 0) {
            if (interestsSets.length === 1 && interest.period === CryptaInterestTypeWeb.all) {
                acc.push({
                    title: isFullTitle ? interestTitleByPeriod[interest.period] : '',
                    segments: text,
                    count: countText
                });
            } else {
                const title = interestsSets.length === 1 ? prefixText : interests;

                acc.push({
                    title: isFullTitle ? interestTitleByPeriod[interest.period] : title,
                    segments: text,
                    count: title.length + countText
                });
            }
        }

        return acc;
    }, []);
};

export const getSortedCheckedInterests = (
    checkedInterestsId: string[],
    interestsTree: InterestsTreeType,
    parentId: string,
    sortedIds: string[]
): string[] => {
    const interests = interestsTree[parentId];

    if (interests && interests.length) {
        interests.forEach(interest => {
            const interestId = interest.id;

            if (checkedInterestsId.includes(interestId)) {
                sortedIds.push(interestId);
            }

            getSortedCheckedInterests(checkedInterestsId, interestsTree, interestId, sortedIds);
        });
    }

    return sortedIds;
};

export const getPredictorPreviewText = (
    segmentsGroup: CryptaSegmentGroup,
    checkedSegments?: string[]
): TextPredictor[] => {
    const result: TextPredictor[] = [];

    if (!checkedSegments) {
        return result;
    }

    const textPreviewRaw = map(segmentsGroup, (segment: SegmentCategory) => {
        segment.segments = getCheckedSegmentsVal(segment, 'name', checkedSegments);

        return segment;
    });
    const textPreview = filter(textPreviewRaw, (segment: SegmentCategory) => segment.segments.length);

    if (textPreview && textPreview.length) {
        map(textPreview, (segment: SegmentCategory) => {
            result.push({
                title: segment.title,
                value: segment.segments.join(', ')
            });
        });
    }

    return result;
};

export const getPredictorInterestsText = (
    interestsIds: InterestsIdsType,
    interestsSets?: InterestsSetType[]
): TextPredictor[] => {
    if (!interestsSets) {
        return [];
    }

    return interestsSets.reduce((acc: TextPredictor[], interest: InterestsSetType, key: number) => {
        const ids = map(interest.checkedList, id => id);
        const text = ids.map(id => interestsIds[id].name).join(', ');

        if (text.length !== 0) {
            acc.push({
                title: getUserProfileConditionTitle(
                    '',
                    UserProfileRetargetingConditionGoalType.INTERESTS,
                    interest.period
                ),
                value: text
            });
        }

        return acc;
    }, []);
};

export const calculateOpenedCategory = (id: GroupId, groupVal: EditGroupDataValue): Action[] => {
    const categoryActions = {
        socialDemo: openCryptaSocialDemoEditor,
        family: openCryptaGroupFamilyEditor,
        behaviors: openCryptaBehavioralEditor,
        interests: openInterestsTargetingEditor
    };
    const openEditorAction = [];
    const isFound = find(audienceTargetingsTypeOrderedList, (category: string) => {
        const categoryVal = get(groupVal, `audienceTargetings.${category}`);

        if (categoryVal && categoryVal.length > 0) {
            openEditorAction.push(categoryActions[category](id, categoryVal));

            return true;
        }

        return false;
    });

    if (!isFound) {
        if (groupVal.campaignType === CampaignType.TEXT) {
            openEditorAction.push(openInterestsTargetingEditor(id, [defaultInterestsTargetingDisabledSetState]));
        } else {
            openEditorAction.push(openCryptaSocialDemoEditor(id, []));
        }
    }

    return openEditorAction;
};

export const getPredictorMetricsText = (conditionData?: RetargetingConditionData): TextPredictor[] => {
    if (!conditionData) {
        return [];
    }

    const rulesGroups = conditionData.rulesGroups;
    const goals = conditionData.goals || [];

    return rulesGroups.reduce((res: TextPredictor[], group: RetargetingConditionDataRulesGroup) => {
        const resultDescription = group.items
            .map(item => {
                if (!item.id) {
                    return '';
                }

                const goal = goals[item.id];

                if (!goal) {
                    return `${i18n('unavailable-goal')} (id:${item.id})`;
                }

                const description = getGoalNameWithDomain(goal);

                if (item.type === RetargetingConditionRuleType.METRIKA_GOAL_RULE) {
                    return item.period !== undefined
                        ? i18n('description-by-day', {
                              count: item.period,
                              description
                          })
                        : description;
                }

                return description;
            })
            .join(', ');

        res.push({
            title: getRetargetingConditionTypeText(group.type),
            value: resultDescription
        });

        return res;
    }, []);
};

export const getAudienceTargetingsEditorDescription = (
    campaignType: CampaignType
): AudienceTargetingsEditorDescription => {
    switch (campaignType) {
        case CampaignType.TEXT:
            return {
                title: (
                    <>
                        {i18n('user-profile')} <BetaMark />
                    </>
                ),
                titleName: i18n('user-profile-name'),
                description: i18n('user-profile-interests-description'),
                noValueDescription: i18n('user-profile-no-value-description')
            };
        case CampaignType.CPM_BANNER:
        default:
            return {
                title: i18n('audience'),
                titleName: i18n('audience-name'),
                description: i18n('audience-description'),
                noValueDescription: i18n('no-value-description')
            };
    }
};

export const renderTextCountRule = (retargetingCondition?: RetargetingConditionData): string => {
    if (!retargetingCondition) {
        return i18n('isNotSet');
    }

    const countGroups = retargetingCondition.rulesGroups.length;

    const countRules = retargetingCondition.rulesGroups.reduce((count, rule) => count + rule.items.length, 0);

    if (countGroups === 0) {
        return i18n('isNotSet');
    } else if (countGroups === 1) {
        return i18n('condition-rules-rule-preview', { count: countRules });
    }

    return i18n('condition-rules-group-preview', { count: countGroups });
};

export const getPreviewMetrikaSegment = (retargetingCondition: RetargetingConditionData): SegmentCategoryText[] => {
    const title = i18n('audience-and-metrika');
    const segments = renderTextCountRule(retargetingCondition);
    return [
        {
            title: title,
            segments: [segments],
            count: title.length + segments.length
        }
    ];
};

export const audienceTargetingsTypeOrderedList = [
    AudienceTargetingsType.socialDemo,
    AudienceTargetingsType.family,
    AudienceTargetingsType.behaviors,
    AudienceTargetingsType.interests,
    AudienceTargetingsType.audienceMetrics
];
